import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { Snackbar, Alert } from "@mui/material";

type ToastSeverity = "success" | "error" | "info" | "warning";

interface ToastContextType {
  showToast: (message: React.ReactNode, severity?: ToastSeverity) => void;
  showSuccessToast: (message: React.ReactNode) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [message, setMessage] = useState<React.ReactNode>(null);
  const [severity, setSeverity] = useState<ToastSeverity>("info");
  const [open, setOpen] = useState(false);

  const showToast = useCallback(
    (msg: React.ReactNode, sev: ToastSeverity = "info") => {
      setMessage(msg);
      setSeverity(sev);
      setOpen(true);
    },
    [setMessage, setSeverity, setOpen],
  );

  const showSuccessToast = useCallback(
    (msg: React.ReactNode) => showToast(msg, "success"),
    [showToast],
  );

  const handleClose = () => setOpen(false);

  return (
    <ToastContext.Provider value={{ showToast, showSuccessToast }}>
      {children}
      <Snackbar
        open={open}
        onClose={handleClose}
        autoHideDuration={severity !== "error" ? 4000 : undefined}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a <ToastProvider />");
  return ctx;
};
